/**
 * What learners have told us we got wrong. F-97.
 *
 * **This screen is the cheap half of F-97 and probably most of its value.** A learner
 * can now say "that was wrong" in the chat, which is worth nothing at all if nobody
 * reads it — that is F-89 exactly, a flag written into a table that no screen shows. So
 * the dispute, the item, the learner's own words and what they were told are all here,
 * on one page, without a query.
 *
 * Two things it deliberately does **not** do.
 *
 * It does not resolve anything. A dispute is `open` and stays `open`: there is no write
 * endpoint for resolving one, and a status this screen could flip without a decision
 * being recorded would be a way of making a complaint disappear. When that write exists
 * it belongs here, behind a `ConfirmedAction` like the other named writes (D-19).
 *
 * It does not judge the dispute. What it shows is the material — the question as it was
 * asked, the key it was marked against, the learner's answer, the teaching that went
 * back — because deciding whether the *learner* or the *system* was right is the one
 * thing a person has to do, and every number on this page exists to make that quick.
 *
 * English-only, no `t()`. See the note at the top of `Console.tsx`.
 */

import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { isError, listDisputes, nextCursorOf, type AdminDispute } from './api';
import { Failed, Id, Nil, When, ago, usePages } from './parts';

/**
 * Why a dispute did not repair the card, in a sentence rather than a field value.
 *
 * The raw reason is the system's own word and is shown beside this, because it is what
 * grep finds in the inbound log. What it needs alongside it is whether the refusal was
 * the design working — a learner cannot appeal a correct mark — or a limit worth knowing
 * about, which `no_snapshot` is: every review state that predates F-97 has one, so the
 * first disputes to arrive will all say this and it is not a fault.
 */
const REFUSALS: Record<string, string> = {
  no_item: 'Nothing marked could be matched to it, so their words are all we have.',
  no_snapshot:
    'The grading predates the pre-state snapshot, so the card could not be put back. Expected on anything graded before F-97 shipped.',
  not_penalised:
    'They were not marked down, so there was no lapse to take back. This is a dispute about the material, not the mark.',
  said_not_knowing: 'They had said they did not know, so the outcome came from their own words.',
  too_old: 'Older than the repair window, so the card was left where it is.',
  awaiting_answer: 'The item had already been sent again, so rewinding it would have double-sent it.',
  raced: 'The row changed between reading it and repairing it, so the repair was declined.',
};

/**
 * Whether this row is one to look at first.
 *
 * A dispute that repaired a card is, by definition, one where the system took the
 * learner's word for it and undid a lapse — which means a grader got it wrong, and the
 * *item* is probably wrong too. A dispute about a correct mark is a claim about the
 * material, which is F-96's territory and is the other thing worth reading. Everything
 * else is ordinary.
 */
function tone(dispute: AdminDispute): 'repaired' | 'fact' | 'plain' {
  if (dispute.repaired) return 'repaired';
  if (dispute.repairRefusedBecause === 'not_penalised') return 'fact';
  return 'plain';
}

function DisputeRows({ rows, timezone }: { rows: AdminDispute[]; timezone?: string }) {
  return (
    <div className="admin-scroll">
      <table className="admin-table">
        <thead>
          <tr>
            <th>When</th>
            <th>What they said</th>
            <th>What they were told</th>
            <th>Card</th>
            <th>Learner</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((dispute) => {
            const kind = tone(dispute);
            return (
              <tr
                key={`${dispute.userId}-${dispute.at}-${dispute.messageId}`}
                className={kind === 'repaired' ? 'is-unanswered' : undefined}
              >
                <td>
                  <When iso={dispute.at} timezone={timezone} />
                  {dispute.questionOpenAtTheTime && (
                    /* Worth seeing: they disputed while a different question was open,
                       which is the case the acknowledgement has to mention or they sit
                       waiting for a mark that is not coming. */
                    <span className="sub">a question was open</span>
                  )}
                </td>

                {/* `wrapped`, not a paragraph: the learner's own line breaks are part of
                    what they said, and this cell is the primary evidence on the page. */}
                <td className="cell-prose">
                  <span className="wrapped strong">{dispute.reason || dispute.said}</span>
                  {dispute.reason && dispute.said !== dispute.reason && (
                    <span className="sub">sent: {dispute.said}</span>
                  )}
                  {!dispute.reason && (
                    <span className="sub">the word alone — no reason given</span>
                  )}
                </td>

                <td className="cell-prose">
                  {dispute.prompt ? (
                    <span className="stack">
                      <span className="wrapped">{dispute.prompt}</span>
                      <span className="sub">
                        key: {dispute.answerKey ?? '(none stored)'} · they answered:{' '}
                        {dispute.theirAnswer ?? '—'}
                      </span>
                      {dispute.ourTeaching && (
                        <span className="wrapped sub">we said: {dispute.ourTeaching}</span>
                      )}
                    </span>
                  ) : (
                    <Nil what="no item matched" />
                  )}
                </td>

                <td>
                  <span className="stack">
                    {dispute.outcome ? (
                      <span className={`tag outcome-${dispute.outcome}`}>{dispute.outcome}</span>
                    ) : (
                      <Nil />
                    )}
                    {dispute.repaired ? (
                      <span className="tag flag">repaired</span>
                    ) : (
                      <span className="sub">not repaired</span>
                    )}
                    {dispute.repaired && dispute.repairedFrom && (
                      <span className="sub">
                        took back {dispute.repairedFrom.lapses !== undefined ? `lapses → ${dispute.repairedFrom.lapses}` : 'the lapse'}
                        {dispute.repairedFrom.due ? `, due → ${ago(dispute.repairedFrom.due)}` : ''}
                      </span>
                    )}
                    {!dispute.repaired && dispute.repairRefusedBecause && (
                      <span className="sub">
                        {REFUSALS[dispute.repairRefusedBecause] ?? dispute.repairRefusedBecause} (
                        <code>{dispute.repairRefusedBecause}</code>)
                      </span>
                    )}
                    {dispute.gradedAt && <span className="sub">marked {ago(dispute.gradedAt)}</span>}
                  </span>
                </td>

                <td>
                  <span className="stack">
                    <Link className="admin-link" to={`/admin/learner/${dispute.userId}`}>
                      {dispute.userId} →
                    </Link>
                    {dispute.itemId && <Id value={dispute.itemId} />}
                    {dispute.objectiveId && dispute.nodeId && (
                      <Link
                        className="admin-link sub"
                        to={`/admin/objective/${dispute.objectiveId}/items?node=${encodeURIComponent(dispute.nodeId)}`}
                      >
                        item →
                      </Link>
                    )}
                    <span className="sub">{dispute.status ?? 'open'}</span>
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The disputes panel, for one learner or for everybody.
 *
 * One component and one `usePages` per instance, which is what keeps the two uses from
 * sharing anything: the learner page's panel and the console-wide screen each hold their
 * own rows, their own cursor, their own loading flag and their own error. A shared error
 * state rendered in two panels has been reported twice on this console and fixed twice —
 * one endpoint failing must not paint a failure across a panel that loaded fine.
 */
export function DisputesPanel({
  userId,
  timezone,
  heading,
}: {
  userId?: string;
  timezone?: string;
  heading?: string;
}) {
  const fetchPage = useCallback(
    async (cursor?: string) => {
      const result = await listDisputes(userId, cursor);
      if (isError(result)) return result;
      return { rows: result.disputes, next: nextCursorOf(result) };
    },
    [userId],
  );
  const { rows, error, loading, done, loadMore, loadAll } = usePages(fetchPage);

  const repaired = rows.filter((dispute) => dispute.repaired).length;
  const facts = rows.filter((dispute) => tone(dispute) === 'fact').length;

  return (
    <section className="admin-panel">
      <header className="admin-head">
        <h2>{heading ?? 'Disputes'}</h2>
        <p className="sub">
          {rows.length} loaded{done ? ', all of them' : ''} · {repaired} repaired a card ·{' '}
          {facts} about the material
        </p>
      </header>

      {error && <Failed error={error} what="Disputes" />}
      {!error && rows.length === 0 && !loading && (
        <p className="admin-empty">
          Nothing disputed. Learners are told the word on a mark they might disagree with —
          three times, then it stops being mentioned — so an empty list here means either
          nobody has objected or nobody has been shown the word yet.
        </p>
      )}

      {rows.length > 0 && <DisputeRows rows={rows} {...(timezone ? { timezone } : {})} />}

      <div className="admin-actions">
        {loading && <span className="sub">loading…</span>}
        {!done && !loading && (
          <>
            <button type="button" className="admin-button quiet" onClick={loadMore}>
              Load more
            </button>
            <button type="button" className="admin-button quiet" onClick={loadAll}>
              Load all
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/** Every learner's disputes, newest first. The screen at `/admin/disputes`. */
export default function Disputes() {
  return (
    <>
      <nav className="crumbs">
        <Link className="admin-link" to="/admin">
          learners
        </Link>
        <span aria-hidden="true">/</span>
        <span>disputes</span>
      </nav>

      <section className="admin-panel">
        <header className="admin-head">
          <h1>Disputes</h1>
          <p className="sub">
            Everything a learner has told us we got wrong, newest first. The two grading
            errors on 2026-09-13 were caught because the owner knows the subject and had a
            chat window; this list is what the next learner has instead.
          </p>
        </header>
        <p className="sub">
          A repaired card means the system took their word for it and undid a lapse — which
          means the item or the grader is probably wrong, and it is the row to read first.
          Nothing here resolves a dispute: there is no write for it yet, and a status this
          screen could flip would be a way to make a complaint vanish.
        </p>
      </section>

      <DisputesPanel heading="All disputes" />
    </>
  );
}
